import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LogOut,
  Wallet,
  Shield,
  Menu,
  X,
  FlaskConical,
  Camera,
  FileCheck,
  Sun,
  Moon,
  Crown,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useTestMode } from "../../contexts/TestModeContext";
import { useTheme } from "../../contexts/ThemeContext";
import { Logo } from "../common/Logo";
import { GTDisplay } from "../common/GTDisplay";
import {
  ProfilePictureUploadModal,
  DEFAULT_AVATAR,
} from "../common/ProfilePictureUploadModal";
import { ThemeToggle } from "../common/ThemeToggle";
const useScrollDirection = () => {
  const [scrollDirection, setScrollDirection] = React.useState<"up" | "down">(
    "up"
  );

  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      const currentY = window.scrollY;
      if (currentY > lastY + 5) setScrollDirection("down");
      else if (currentY < lastY - 5) setScrollDirection("up");
      lastY = currentY;
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return scrollDirection;
};
export const Header: React.FC = () => {
  const { user, wallet, isAdmin, logout } = useAuth();
  const { isTestMode, toggleTestMode } = useTestMode();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const scrollDirection = useScrollDirection();

  // Get the user's photo URL or default avatar
  const userPhotoURL = user?.photoURL || DEFAULT_AVATAR;

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  const navLinks = [
    { path: "/og-hub", label: "OG Hub", icon: Crown },

    { path: "/wallet", label: "Wallet", icon: Wallet },
    ...(isAdmin
      ? [
          { path: "/verification", label: "Verification", icon: FileCheck },
          { path: "/admin", label: "Admin", icon: Shield },
        ]
      : []),
  ];

  const isActive = (path: string) => location.pathname === path;

  // Calculate top offset when test mode banner is shown
  const topOffset = isTestMode ? "top-[40px]" : "top-0";

  return (
    <motion.header
      animate={{
        y: scrollDirection === "down" ? -80 : 0,
      }}
      transition={{ type: "tween", duration: 0.25 }}
      className={`sticky ${topOffset} z-40 bg-white/80 dark:bg-background-dark/80 backdrop-blur-lg border-b border-border-light dark:border-border-dark`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo - Links to home */}
          <Link to="/" className="flex-shrink-0">
            <Logo size="md" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map(({ path, label, icon: Icon }) => (
              <Link
                key={path}
                to={path}
                className={`
                  flex items-center gap-2 px-4 py-2 rounded-button font-medium transition-all duration-200
                  ${
                    isActive(path)
                      ? "bg-primary/10 text-primary"
                      : "text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-light dark:hover:bg-surface-dark hover:text-text-primary-light dark:hover:text-text-primary-dark"
                  }
                `}
              >
                <Icon size={18} />
                {label}
              </Link>
            ))}
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-4">
            {/* Theme Toggle */}
            <ThemeToggle size="sm" className="hidden md:block" />

            {/* Test Mode Toggle (Admin Only) */}
            {isAdmin && (
              <button
                onClick={toggleTestMode}
                className={`
                  hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold transition-all
                  ${
                    isTestMode
                      ? "bg-warning text-black ring-2 ring-warning/50"
                      : "bg-gray-100 dark:bg-gray-800 text-text-secondary-light dark:text-text-secondary-dark hover:bg-gray-200 dark:hover:bg-gray-700"
                  }
                `}
                title={isTestMode ? "Disable Test Mode" : "Enable Test Mode"}
              >
                <FlaskConical size={14} />
                {isTestMode ? "TEST MODE" : "Test"}
              </button>
            )}

            {/* GT Balance - Hide on landing page */}
            {wallet && location.pathname !== "/" && (
              <Link to="/wallet">
                <GTDisplay amount={wallet.balance} size="sm" animated />
              </Link>
            )}

            {/* User Menu */}
            <div className="hidden md:flex items-center gap-3">
              {user && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowProfileModal(true)}
                    className="relative group"
                    title="Change profile picture"
                  >
                    <img
                      src={userPhotoURL}
                      alt={user.username}
                      className="w-8 h-8 rounded-full object-cover ring-2 ring-primary/20 group-hover:ring-primary/50 transition-all"
                    />
                    <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Camera size={14} className="text-white" />
                    </div>
                  </button>
                  <span className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
                    {user.username}
                  </span>
                </div>
              )}

              <button
                onClick={handleLogout}
                className="p-2 rounded-button text-text-secondary-light dark:text-text-secondary-dark hover:bg-error/10 hover:text-error transition-colors"
                title="Logout"
              >
                <LogOut size={18} />
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-button text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-light dark:hover:bg-surface-dark transition-colors"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <motion.div
        initial={false}
        animate={{
          height: mobileMenuOpen ? "auto" : 0,
          opacity: mobileMenuOpen ? 1 : 0,
        }}
        className="md:hidden overflow-hidden bg-white dark:bg-background-dark border-b border-border-light dark:border-border-dark"
      >
        <div className="px-4 py-4 space-y-2">
          {user && (
            <div className="flex items-center gap-3 pb-4 border-b border-border-light dark:border-border-dark">
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  setShowProfileModal(true);
                }}
                className="relative group flex-shrink-0"
              >
                <img
                  src={userPhotoURL}
                  alt={user.username}
                  className="w-10 h-10 rounded-full object-cover ring-2 ring-primary/20 group-hover:ring-primary/50 transition-all"
                />
                <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Camera size={16} className="text-white" />
                </div>
              </button>
              <div>
                <p className="font-medium text-text-primary-light dark:text-text-primary-dark">
                  {user.displayName || user.username}
                </p>
                <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                  @{user.username}
                </p>
              </div>
            </div>
          )}

          {navLinks.map(({ path, label, icon: Icon }) => (
            <Link
              key={path}
              to={path}
              onClick={() => setMobileMenuOpen(false)}
              className={`
                flex items-center gap-3 px-4 py-3 rounded-button font-medium transition-all duration-200
                ${
                  isActive(path)
                    ? "bg-primary/10 text-primary"
                    : "text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-light dark:hover:bg-surface-dark"
                }
              `}
            >
              <Icon size={20} />
              {label}
            </Link>
          ))}

          {/* Theme Toggle - Mobile */}
          <div className="flex items-center justify-between px-4 py-3 rounded-button text-text-secondary-light dark:text-text-secondary-dark">
            <span className="font-medium">
              {isDark ? "Night Game" : "Day Game"}
            </span>
            <ThemeToggle size="sm" />
          </div>

          {/* Test Mode Toggle (Mobile - Admin Only) */}
          {isAdmin && (
            <button
              onClick={toggleTestMode}
              className={`
                w-full flex items-center gap-3 px-4 py-3 rounded-button font-medium transition-all
                ${
                  isTestMode
                    ? "bg-warning/20 text-warning"
                    : "text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-light dark:hover:bg-surface-dark"
                }
              `}
            >
              <FlaskConical size={20} />
              {isTestMode ? "Test Mode ON" : "Enable Test Mode"}
            </button>
          )}

          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-button font-medium text-error hover:bg-error/10 transition-colors"
          >
            <LogOut size={20} />
            Logout
          </button>
        </div>
      </motion.div>

      {/* Profile Picture Upload Modal */}
      <ProfilePictureUploadModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        currentPhotoURL={user?.photoURL}
      />
    </motion.header>
  );
};

export default Header;
